import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams, useNavigate} from 'react-router-dom';
import moment from 'moment';
import {Confirm, Loading, Notify} from 'notiflix';
import {Card, Grid, Divider, Icon, IconButton, Tooltip, Box, CircularProgress} from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import {errorLogsApi} from 'api/errorLogsApi';

const statusBadgeColor = statusCode => {
  if (!statusCode) return 'secondary';
  if (statusCode >= 500) return 'error';
  if (statusCode >= 400) return 'warning';
  return 'success';
};

const MetaRow = ({label, value}) => (
  <Grid item xs={12} sm={6} md={4}>
    <MDTypography variant="caption" color="text" fontWeight="bold" display="block">
      {label}
    </MDTypography>
    <MDTypography variant="button">{value ?? '—'}</MDTypography>
  </Grid>
);

const ErrorLogDetails = () => {
  const {id} = useParams();
  const navigate = useNavigate();
  const {user} = useSelector(state => state.auth);

  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchLog = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await errorLogsApi.fetchLogById(user?.accessToken, id);
      setLog(data);
    } catch (err) {
      console.error('Error fetching log details:', err);
      setError('Failed to load error log details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleCopyStack = async () => {
    try {
      await navigator.clipboard.writeText(log?.stack || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      Notify.failure('Failed to copy to clipboard');
    }
  };

  const handleDelete = () => {
    Confirm.show(
      'Delete Error Log?',
      'Are you sure you want to delete this log entry? This action cannot be undone.',
      'Delete',
      'Cancel',
      async () => {
        try {
          Loading.circle('Deleting...');
          await errorLogsApi.deleteLog(user?.accessToken, id);
          Loading.remove();
          Notify.success('Log deleted');
          navigate('/error-logs');
        } catch (err) {
          Loading.remove();
          Notify.failure(err?.response?.data?.message || 'Failed to delete log');
        }
      },
      () => {},
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox mt={5} display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Box textAlign="center">
            <CircularProgress size="3rem" color="inherit" />
            <MDTypography variant="h6" mt={2}>
              Loading log details...
            </MDTypography>
          </Box>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error || !log) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox mt={5} mb={3}>
          <Card sx={{padding: 4, textAlign: 'center'}}>
            <Icon sx={{fontSize: 48, color: 'error.main', mb: 2}}>error_outline</Icon>
            <MDTypography variant="h5" mb={2}>
              {error || 'Log not found'}
            </MDTypography>
            <MDButton variant="contained" onClick={() => navigate('/error-logs')}>
              Back to Error Logs
            </MDButton>
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={5} mb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12} sx={{display: 'flex'}}>
            <Divider orientation="vertical" sx={{ml: -2, mr: 1}} />
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                  }}>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Tooltip title="Back">
                      <IconButton color="white" onClick={() => navigate('/error-logs')}>
                        <Icon sx={{color: 'white'}}>arrow_back</Icon>
                      </IconButton>
                    </Tooltip>
                    <MDTypography variant="h6" color="white">
                      Error Log Details
                    </MDTypography>
                  </MDBox>
                  <MDButton variant="contained" color="error" onClick={handleDelete}>
                    Delete
                  </MDButton>
                </MDBox>

                <MDBox p={3}>
                  <Grid container spacing={2} mb={3}>
                    <Grid item xs={12} sm={6} md={4}>
                      <MDTypography variant="caption" color="text" fontWeight="bold" display="block">
                        Status Code
                      </MDTypography>
                      {log.statusCode ? (
                        <MDBadge
                          badgeContent={log.statusCode}
                          max={999}
                          color={statusBadgeColor(log.statusCode)}
                          variant="gradient"
                          size="sm"
                        />
                      ) : (
                        <MDTypography variant="button">—</MDTypography>
                      )}
                    </Grid>
                    <MetaRow label="Error Type" value={log.errorType} />
                    <MetaRow label="Method" value={log.method} />
                    <MetaRow label="Path" value={log.path} />
                    <MetaRow label="User ID" value={log.userId} />
                    <MetaRow label="Environment" value={log.environment} />
                    <MetaRow
                      label="Created At"
                      value={log.createdAt ? moment(log.createdAt).format('DD MMM YYYY, HH:mm:ss') : '—'}
                    />
                  </Grid>

                  <Divider />

                  <MDBox mt={3}>
                    <MDTypography variant="h6" fontWeight="medium" mb={1}>
                      Message
                    </MDTypography>
                    <MDTypography variant="button" sx={{whiteSpace: 'pre-wrap'}}>
                      {log.message}
                    </MDTypography>
                  </MDBox>

                  <MDBox mt={3}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <MDTypography variant="h6" fontWeight="medium">
                        Stack Trace
                      </MDTypography>
                      <MDButton variant="outlined" color="info" size="small" onClick={handleCopyStack}>
                        <Icon sx={{mr: 0.5}}>content_copy</Icon>
                        {copied ? 'Copied!' : 'Copy'}
                      </MDButton>
                    </MDBox>
                    <Box
                      component="pre"
                      sx={{
                        backgroundColor: '#1e1e1e',
                        color: '#d4d4d4',
                        p: 2,
                        borderRadius: 1,
                        maxHeight: 500,
                        overflow: 'auto',
                        fontSize: '0.8rem',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}>
                      {log.stack || 'No stack trace available'}
                    </Box>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Divider orientation="vertical" sx={{mx: 0}} />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default ErrorLogDetails;
